import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { TrendingUp, TrendingDown, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react'
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Legend,
} from 'recharts'
import { api, formatMoney } from '@/lib/api'

export default function FluxoCaixaPage() {
  const [dias, setDias] = useState(30)

  const { data: fc, isLoading } = useQuery<any>({
    queryKey: ['fluxo-caixa', dias],
    queryFn: async () => (await api.get('/relatorios/fluxo-caixa', { params: { dias } })).data,
  })

  if (isLoading) return <div className="text-ink-500">Carregando fluxo de caixa...</div>
  if (!fc) return null

  const fmtDia = (v: string) => {
    const d = new Date(v + 'T00:00')
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <label className="label">Horizonte</label>
          <select
            className="input max-w-[180px]"
            value={dias}
            onChange={(e) => setDias(Number(e.target.value))}
          >
            <option value={7}>7 dias</option>
            <option value={15}>15 dias</option>
            <option value={30}>30 dias</option>
            <option value={60}>60 dias</option>
            <option value={90}>90 dias</option>
          </select>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPI
          label="Saldo Atual"
          value={formatMoney(fc.saldo_atual)}
          tone={fc.saldo_atual >= 0 ? 'green' : 'red'}
          icon={fc.saldo_atual >= 0 ? ArrowUp : ArrowDown}
        />
        <KPI label="A Receber" value={formatMoney(fc.total_a_receber)} tone="brand" icon={TrendingUp} />
        <KPI label="A Pagar" value={formatMoney(fc.total_a_pagar)} tone="amber" icon={TrendingDown} />
        <KPI
          label="Saldo Projetado"
          value={formatMoney(fc.saldo_projetado)}
          tone={fc.saldo_projetado >= 0 ? 'green' : 'red'}
          icon={fc.saldo_projetado >= 0 ? ArrowUp : ArrowDown}
        />
      </div>

      {/* Alerta de vencidos */}
      {fc.vencidos > 0 && (
        <div className="card card-body bg-red-50 border-red-200 text-red-800 text-sm font-medium flex items-center gap-2">
          <AlertTriangle size={18} />
          Atencao: {formatMoney(fc.vencidos)} em titulos vencidos a receber.
        </div>
      )}

      {/* Grafico REALIZADO */}
      {fc.realizado?.length > 0 ? (
        <div className="card card-body">
          <h3 className="font-semibold text-ink-900 mb-3">
            Fluxo Realizado — Ultimos {dias} dias
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={fc.realizado}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eceef2" />
                <XAxis dataKey="data" tick={{ fontSize: 10 }} tickFormatter={fmtDia} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => 'R$' + (v / 1000).toFixed(0) + 'k'} />
                <Tooltip
                  formatter={(v: any) => formatMoney(v)}
                  labelFormatter={(v) => new Date(v + 'T00:00').toLocaleDateString('pt-BR')}
                />
                <Legend />
                <Bar dataKey="receitas" fill="#10b981" name="Receitas" radius={[4, 4, 0, 0]} />
                <Bar dataKey="despesas" fill="#ef4444" name="Despesas" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="card card-body text-center text-ink-500 text-sm">
          Sem movimentacao nos ultimos {dias} dias.
        </div>
      )}

      {/* Tabela detalhada REALIZADA
          FIX 30/06: usuário reclamou que uma saída (despesa de caixa) constava no DRE
          mas "não aparecia" no Fluxo de Caixa. Causa: o bloco Realizado só tinha gráfico
          de barras (uma saída de R$100 some entre receitas de milhares) e não havia
          tabela com valores — a única tabela com coluna "Saídas" era a do Projetado, que
          lê só contas a pagar em aberto (vazia). O dado SEMPRE esteve aqui (mesma fonte do
          DRE: lancamentos_financeiros); faltava exibi-lo como número. Esta tabela mostra. */}
      {fc.realizado?.length > 0 && (
        <div className="card card-body">
          <h3 className="font-semibold text-ink-900 mb-3">Detalhamento — Realizado</h3>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th className="text-right">Entradas</th>
                  <th className="text-right">Saidas</th>
                  <th className="text-right">Saldo do dia</th>
                </tr>
              </thead>
              <tbody>
                {fc.realizado.map((p: any) => (
                  <tr key={p.data}>
                    <td>{new Date(p.data + 'T00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })}</td>
                    <td className="text-right text-emerald-600">{p.receitas > 0 ? formatMoney(p.receitas) : '-'}</td>
                    <td className="text-right text-red-600">{p.despesas > 0 ? formatMoney(p.despesas) : '-'}</td>
                    <td className={`text-right font-bold ${p.saldo >= 0 ? 'text-ink-900' : 'text-red-600'}`}>{formatMoney(p.saldo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Grafico PROJETADO */}
      {fc.projetado?.length > 0 ? (
        <div className="card card-body">
          <h3 className="font-semibold text-ink-900 mb-3">
            Fluxo Projetado — Proximos {dias} dias
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={fc.projetado}>
                <defs>
                  <linearGradient id="gp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0069c4" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#0069c4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eceef2" />
                <XAxis dataKey="data" tick={{ fontSize: 10 }} tickFormatter={fmtDia} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => 'R$' + (v / 1000).toFixed(0) + 'k'} />
                <Tooltip
                  formatter={(v: any) => formatMoney(v)}
                  labelFormatter={(v) => new Date(v + 'T00:00').toLocaleDateString('pt-BR')}
                />
                <Area
                  type="monotone"
                  dataKey="saldo_projetado"
                  stroke="#0069c4"
                  strokeWidth={2}
                  fill="url(#gp)"
                  name="Saldo Projetado"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-ink-500 mt-2">
            Projecao considera contas a receber e pagar em aberto, com vencimento nos proximos {dias} dias.
          </p>
        </div>
      ) : (
        <div className="card card-body text-center text-ink-500 text-sm">
          Sem previsao de movimentacao nos proximos {dias} dias.
        </div>
      )}

      {/* Tabela detalhada PROJETADA */}
      {fc.projetado?.length > 0 && (
        <div className="card card-body">
          <h3 className="font-semibold text-ink-900 mb-3">Detalhamento — Projetado</h3>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th className="text-right">Entradas</th>
                  <th className="text-right">Saidas</th>
                  <th className="text-right">Saldo Projetado</th>
                </tr>
              </thead>
              <tbody>
                {fc.projetado.map((p: any) => (
                  <tr key={p.data}>
                    <td>{new Date(p.data + 'T00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })}</td>
                    <td className="text-right text-emerald-600">{p.receitas > 0 ? formatMoney(p.receitas) : '-'}</td>
                    <td className="text-right text-red-600">{p.despesas > 0 ? formatMoney(p.despesas) : '-'}</td>
                    <td className="text-right font-bold">{formatMoney(p.saldo_projetado)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function KPI({
  label, value, tone, icon: Icon,
}: {
  label: string
  value: string
  tone: 'brand' | 'green' | 'amber' | 'red'
  icon: any
}) {
  const toneClasses: Record<string, string> = {
    brand: 'bg-brand-50 text-brand-600',
    green: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
  }
  return (
    <div className="card card-body">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-ink-500 uppercase tracking-wider">{label}</div>
          <div className="text-xl font-bold text-ink-900 mt-1">{value}</div>
        </div>
        <div className={`p-2 rounded-lg ${toneClasses[tone]}`}>
          <Icon size={16} />
        </div>
      </div>
    </div>
  )
}
